import { useEffect, useState } from 'react';
import { describeSite } from '../../store/ForNeighborsApi';
import { Coordinate, Site } from '../../types/Site';
import { logError } from '../../utils/logger';
import distance from '../../utils/distance';

export type SiteFetchErrorKind = 'notFound' | 'forbidden' | 'server' | 'network' | 'sessionExpired';

export interface SiteFetchError {
  kind: SiteFetchErrorKind;
}

export interface UseSiteResult {
  site: Site | null;
  loading: boolean;
  error: SiteFetchError | null;
  // Miles from the neighbor's own location to this site. Computed here because no Sites operation
  // vends a neighbor-relative distance: DescribeSite is addressed by id alone and has no center to
  // measure from, so its Site carries no distance at all (see ForNeighborsApi.ts's describeSite).
  // Null while the site or userCoordinate is unavailable -- callers show no distance rather than a
  // fabricated one.
  distanceMiles: number | null;
}

function classifySiteFetchError(e: unknown): SiteFetchError {
  // Session establishment (checkSession -> establishSession) fails before this call's own
  // request is even sent, for reasons unrelated to the sites endpoint's own status codes -- keyed
  // on name rather than status so it covers every EstablishSessionError variant, not just the
  // token-rejected/401 case.
  if ((e as Error)?.name === 'EstablishSessionError') return { kind: 'sessionExpired' };
  const status = (e as { status?: number })?.status;
  if (status === 404) return { kind: 'notFound' };
  // A 401 that didn't come from EstablishSessionError is the endpoint itself rejecting the
  // X-UIDH session header -- the same "please re-authenticate" remedy as a session
  // establishment failure, not a 403's "you may not do this", so it's sessionExpired too.
  if (status === 401) return { kind: 'sessionExpired' };
  if (status === 403) return { kind: 'forbidden' };
  if (status && status >= 500) return { kind: 'server' };
  return { kind: 'network' };
}

export function useSite(siteId: string, isLoggedIn: boolean, userCoordinate?: Coordinate): UseSiteResult {
  const [site, setSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<SiteFetchError | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSite(null);

    (async () => {
      try {
        // isLoggedIn mirrors Api.getPantryByOrganizationId's isLoggedIn param -- neighbors can
        // view site details without an account, so this must not unconditionally require a session.
        const response = await describeSite(siteId, isLoggedIn);
        if (cancelled) return;
        setSite(response.site);
      } catch (e) {
        if (cancelled) return;
        logError(e as Error);
        setError(classifySiteFetchError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [siteId, isLoggedIn]);

  // distance() returns statute miles by default (no unit arg) -- reused as-is from the legacy
  // pantry flow rather than adding a second, meters-based distance formula.
  const distanceMiles =
    site && userCoordinate
      ? distance(
          userCoordinate.latitude,
          userCoordinate.longitude,
          site.coordinate.latitude,
          site.coordinate.longitude
        )
      : null;

  return { site, loading, error, distanceMiles };
}
